/**
 * GtagIntegration - Google Consent Mode v2 bridge
 *
 * cookieconsent v3 has NO built-in GCM support.
 * We wire it manually: set default denied, then update on consent action.
 *
 * No-ops gracefully when gtag is not present on the page.
 */

import type { GtagConsentState } from './types';

declare const __CONSENTPOP_DEBUG__: boolean;

// Category → GCM consent type mapping
const CATEGORY_MAP: Record<string, (keyof GtagConsentState)[]> = {
  analytics: ['analytics_storage'],
  marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  functionality: ['functionality_storage', 'personalization_storage'],
};

export class GtagIntegration {
  /**
   * Set all consent types to 'denied' by default.
   * Must be called BEFORE CookieConsent.run() and BEFORE gtag loads.
   */
  setDefaultDenied(): void {
    if (typeof window === 'undefined') return;
    const consentWindow = window as typeof window & {
      __consentpopGcmDefaultSet?: boolean;
    };
    if (consentWindow.__consentpopGcmDefaultSet) return;
    consentWindow.__consentpopGcmDefaultSet = true;

    if (!this.hasGtag()) {
      // gtag not loaded yet — push to dataLayer instead (gtag will pick it up)
      this.pushToDataLayer('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted', // always granted
        wait_for_update: 500, // wait 500ms for consent banner
      });
      return;
    }

    this.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500,
    });

    if (typeof __CONSENTPOP_DEBUG__ !== 'undefined' && __CONSENTPOP_DEBUG__) {
      console.debug('[ConsentPop] GCM v2: default denied state set');
    }
  }

  /**
   * Update consent state based on accepted categories.
   * Returns the consent state object for logging.
   */
  updateFromCategories(acceptedCategories: string[]): Record<string, string> {
    const consentState: Record<string, string> = {
      security_storage: 'granted',
    };

    // Map each category to its GCM consent types
    for (const [category, consentTypes] of Object.entries(CATEGORY_MAP)) {
      const granted = acceptedCategories.includes(category);
      for (const type of consentTypes) {
        consentState[type] = granted ? 'granted' : 'denied';
      }
    }

    // Ensure all types have a value (default denied)
    const allTypes: (keyof GtagConsentState)[] = [
      'analytics_storage', 'ad_storage', 'ad_user_data',
      'ad_personalization', 'functionality_storage', 'personalization_storage',
    ];
    for (const type of allTypes) {
      if (!consentState[type]) {
        consentState[type] = 'denied';
      }
    }

    // Update via gtag or dataLayer
    if (this.hasGtag()) {
      this.gtag('consent', 'update', consentState);
    } else {
      this.pushToDataLayer('consent', 'update', consentState);
    }

    if (typeof __CONSENTPOP_DEBUG__ !== 'undefined' && __CONSENTPOP_DEBUG__) {
      console.debug('[ConsentPop] GCM v2: consent updated', consentState);
    }

    return consentState;
  }

  private gtag(...args: unknown[]): void {
    try {
      (window as any).gtag(...args);
    } catch { /* silently ignore */ }
  }

  private hasGtag(): boolean {
    return typeof window !== 'undefined' && typeof (window as any).gtag === 'function';
  }

  private pushToDataLayer(...args: unknown[]): void {
    try {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push(args);
    } catch { /* silently ignore */ }
  }
}
